import { memo } from "react";

import { Box, Flex, Text, Heading, VStack, HStack, Badge, Tooltip, Portal } from "@libs/design";
import { CircleHelp } from "@assets/icons";

export type ValidationSummaryProps = {
  data: Array<{
    type: string;
    passed: number;
    failed: number;
    total: number;
  }>;
};

const FONT_SIZE = 13;

const VALIDATION_TOOLTIPS: Record<string, { title: string; body: string }> = {
  Temporal: {
    title: "Temporal Checks",
    body: "Monitors whether data arrives on schedule and meets SLA targets. Highlights late loads, and missing reporting days.",
  },
  Duplicates: {
    title: "Duplicates",
    body: "Identifies repeated records within the dataset. Shows duplicate ratios, key conflict fields, and trends to prevent reporting inflation or double counting.",
  },
  Validity: {
    title: "Validity",
    body: "Verifies that data values meet business rules and format standards. Flags out-of-range values, invalid codes, null mandatory fields, and incorrect dates.",
  },
  Referential: {
    title: "Referential",
    body: "Checks consistency of references between datasets. Ensures foreign keys and linked records are valid.",
  },
  "Cross-Source": {
    title: "Cross-Source",
    body: "Validates consistency across different data sources. Identifies mismatches and conflicts between sources.",
  },
  Holiday: {
    title: "Holiday",
    body: "Validates data availability against country-specific holiday calendars.",
  },
};

export const ValidationSummary = memo(function ValidationSummary({ data }: ValidationSummaryProps) {
  const colors = {
    background: "#F3F4F6",
    progress: "#10B981",
    label: "#374151",
    passed: "#10B981",
    divider: "#9CA3AF",
    total: "#6B7280",
    failedBg: "#FEF2F2",
    failedText: "#DC2626",
    successBg: "#ECFDF5",
    successText: "#059669",
  };

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading fontSize="md" fontWeight="semibold" color="gray.900">
            Validation Summary
          </Heading>
          <Text fontSize="xs" color="gray.500">
            Today&apos;s validation run results
          </Text>
        </Box>
      </Flex>
      <VStack gap={5} align="stretch">
        {data.map((item, i) => {
          const percentage = item.total > 0 ? (item.passed / item.total) * 100 : 0;

          const hasFailures = item.failed > 0;

          const badgeBg = hasFailures ? colors.failedBg : colors.successBg;

          const badgeText = hasFailures ? colors.failedText : colors.successText;

          const tooltipContent = VALIDATION_TOOLTIPS[item.type] ?? {
            title: item.type,
            body: "Validation check results.",
          };
          const tooltipAriaLabel = `${tooltipContent.title}. ${tooltipContent.body}`;

          return (
            <Flex key={i} align="center" gap={4}>
              <HStack w={32} gap={1.5} flexShrink={0} align="center" fontWeight="medium">
                <Text style={{ color: colors.label, fontSize: FONT_SIZE }}>{item.type}</Text>
                <Tooltip.Root positioning={{ placement: "top" }} openDelay={300} closeDelay={100}>
                  <Tooltip.Trigger asChild>
                    <Box
                      as="span"
                      display="inline-flex"
                      alignItems="center"
                      cursor="help"
                      style={{ color: colors.label }}
                      aria-label={tooltipAriaLabel}
                    >
                      <CircleHelp size={10} strokeWidth={2} />
                    </Box>
                  </Tooltip.Trigger>
                  <Portal>
                    <Tooltip.Positioner>
                      <Tooltip.Content
                        maxW="sm"
                        bg="white"
                        borderWidth="1px"
                        borderColor="gray.200"
                        borderRadius="lg"
                        boxShadow="sm"
                        px={4}
                        py={3}
                        lineHeight="tall"
                      >
                        <Text fontWeight="semibold" color="gray.900" fontSize="sm" mb={2}>
                          {tooltipContent.title}
                        </Text>
                        <Text color="gray.600" fontSize="xs" lineHeight="tall">
                          {tooltipContent.body}
                        </Text>
                      </Tooltip.Content>
                    </Tooltip.Positioner>
                  </Portal>
                </Tooltip.Root>
              </HStack>
              <Box flex={1} minW={0}>
                <Box
                  h={2.5}
                  borderRadius="full"
                  overflow="hidden"
                  position="relative"
                  style={{ backgroundColor: colors.background }}
                >
                  <Box
                    h="100%"
                    borderRadius="full"
                    w={`${percentage}%`}
                    transition="all 0.3s ease"
                    style={{ backgroundColor: colors.progress }}
                  />
                </Box>
              </Box>
              <HStack gap={2} flexShrink={0} align="center">
                <Text
                  fontWeight="semibold"
                  minW="fit-content"
                  style={{ color: colors.passed, fontSize: FONT_SIZE }}
                >
                  {item.passed}
                </Text>
                <Text style={{ color: colors.divider, fontSize: FONT_SIZE }}>/</Text>
                <Text
                  fontWeight="medium"
                  minW="fit-content"
                  style={{ color: colors.total, fontSize: FONT_SIZE }}
                >
                  {item.total}
                </Text>
                <Badge
                  fontWeight="medium"
                  px={3}
                  py={1.5}
                  borderRadius="md"
                  border="none"
                  boxShadow="none"
                  style={{
                    fontSize: FONT_SIZE,
                    backgroundColor: badgeBg,
                    color: badgeText,
                  }}
                >
                  {item.failed} Failed
                </Badge>
              </HStack>
            </Flex>
          );
        })}
      </VStack>
    </Box>
  );
});
